/**
 * Named signal bus: rig definitions → live float signals (0–1) with decay/smoothing.
 * Program change is handled separately (not a signal).
 */
import { MIDI_CONSTANTS } from '../midi/MIDIConstants.js';

const STATUS = MIDI_CONSTANTS.messageTypes;

export class SignalBus {
  constructor() {
    /** @type {Record<string, import('../performance/schema.js').RigSignalDef & { release?: number }>} */
    this._defs = {};
    /** @type {Record<string, number>} */
    this.signals = {};
    /** @type {{ bpmPulse?: number, bpmBuiltin?: Record<string, number>, audioLevel?: number, frequencyData?: Uint8Array | null, sampleRate?: number, fftSize?: number }} */
    this._frame = {};
    this.onProgramChange = null; // (program, channel0) => void
  }

  /**
   * @param {import('../performance/schema.js').RigSignalDef} def
   */
  _defaultRelease(def) {
    if (def.release != null) return def.release;
    if (def.type === 'midi_cc' || def.type === 'audio_rms' || def.type === 'audio_band' || def.type === 'engine')
      return 0;
    return 200;
  }

  applyRig(rig) {
    this._defs = {};
    this.signals = {};
    const signals = rig?.signals || {};
    for (const [name, def] of Object.entries(signals)) {
      if (!def || typeof def !== 'object') continue;
      this._defs[name] = { ...def, release: this._defaultRelease(def) };
      this.signals[name] = 0;
    }
  }

  /**
   * @param {{ bpmPulse?: number, bpmBuiltin?: Record<string, number>, audioLevel?: number, frequencyData?: Uint8Array | null, sampleRate?: number, fftSize?: number }} frame
   */
  setFrameInputs(frame) {
    this._frame = { ...this._frame, ...frame };
  }

  /**
   * @param {Uint8Array} data
   */
  onMidiMessage(data) {
    const rawStatus = data[0];
    if (rawStatus >= 0xf0) {
      if (rawStatus >= 0xf8) return;
      return;
    }

    const status = rawStatus & 0xf0;
    const channel0 = rawStatus & 0x0f;
    const channel1 = channel0 + 1;

    if (status === STATUS.programChange) {
      const program = data[1] & 0x7f;
      this.onProgramChange?.(program, channel0);
      return;
    }

    if (status === STATUS.controlChange) {
      const cc = data[1];
      const value = data[2];
      const norm = value / MIDI_CONSTANTS.ranges.controllers.max;
      for (const [name, def] of Object.entries(this._defs)) {
        if (def.type !== 'midi_cc') continue;
        if (def.channel !== channel1 || def.cc !== cc) continue;
        this.signals[name] = norm;
      }
      return;
    }

    if (status === STATUS.noteOn || status === STATUS.noteOff) {
      const note = data[1];
      const vel = data[2];
      const isOn = status === STATUS.noteOn && vel > 0;
      const norm = isOn ? vel / MIDI_CONSTANTS.ranges.velocity.max : 0;

      for (const [name, def] of Object.entries(this._defs)) {
        if (def.type !== 'midi_note') continue;
        if (def.channel !== channel1) continue;
        if (def.note != null && def.note !== note) continue;
        if (isOn) {
          this.signals[name] = Math.max(this.signals[name] || 0, norm);
        } else if (def.release === 0) {
          this.signals[name] = 0;
        }
      }
    }
  }

  /**
   * @param {number} deltaSec
   */
  update(deltaSec) {
    const { bpmPulse = 0, audioLevel = 0, frequencyData, sampleRate, fftSize } = this._frame;

    for (const [name, def] of Object.entries(this._defs)) {
      if (def.type === 'engine' && name === 'bpm_pulse') {
        this.signals[name] = bpmPulse;
        continue;
      }
      if (def.type === 'audio_rms') {
        const smooth = def.release > 0 ? Math.min(1, deltaSec / (def.release / 1000)) : 0.15;
        const target = Math.max(0, Math.min(1, audioLevel));
        const cur = this.signals[name] || 0;
        this.signals[name] = cur + (target - cur) * (smooth || 0.2);
        continue;
      }
      if (def.type === 'audio_band' && frequencyData?.length && sampleRate && fftSize) {
        const e = this._bandEnergy(frequencyData, sampleRate, fftSize, def.band);
        const smooth = 0.2;
        const cur = this.signals[name] || 0;
        this.signals[name] = cur + (e - cur) * smooth;
        continue;
      }
      if (def.type === 'midi_note') {
        const relMs = def.release || 200;
        if (relMs <= 0) continue;
        const v = this.signals[name] || 0;
        if (v <= 0) continue;
        const tau = relMs / 1000;
        this.signals[name] = v * Math.pow(0.001, deltaSec / Math.max(tau, 0.01));
        if (this.signals[name] < 0.0005) this.signals[name] = 0;
        continue;
      }
      if (def.type === 'midi_cc') {
        // hold value
        continue;
      }
      if (def.type === 'engine') {
        continue;
      }
    }

    const bb = this._frame.bpmBuiltin;
    if (bb && typeof bb === 'object') {
      for (const [k, v] of Object.entries(bb)) {
        if (typeof v === 'number' && !Number.isNaN(v)) this.signals[k] = v;
      }
    }
  }

  /**
   * @param {Uint8Array} frequencyData
   * @param {number} sampleRate
   * @param {number} fftSize
   * @param {string} [band]
   */
  _bandEnergy(frequencyData, sampleRate, fftSize, band) {
    const nyquist = sampleRate / 2;
    const binHz = nyquist / frequencyData.length;
    const range = (f0, f1) => {
      let i0 = Math.floor(f0 / binHz);
      let i1 = Math.ceil(f1 / binHz);
      i0 = Math.max(0, Math.min(frequencyData.length - 1, i0));
      i1 = Math.max(0, Math.min(frequencyData.length, i1));
      if (i1 <= i0) return 0;
      let s = 0;
      for (let i = i0; i < i1; i++) s += frequencyData[i];
      return (s / (i1 - i0)) / 255;
    };

    switch (band) {
      case 'bass':
        return range(20, 250);
      case 'lowMid':
        return range(250, 500);
      case 'mid':
        return range(500, 2000);
      case 'highMid':
        return range(2000, 6000);
      case 'treble':
        return range(6000, 14000);
      default:
        return range(20, 2000);
    }
  }
}
